import { lazy, Suspense, startTransition, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Clock, Server, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { useAudit } from '@/hooks/useAudit';
import { Loader } from '@/components/ui/loader';
import { fmtDate, statusBadgeVariant, STATUS_KEY } from '@/lib/utils';
import { Button, buttonClassName } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { callDeleteAudit } from '@/lib/functions';
import { Dialog } from '@/components/ui/dialog';
import { BackLink } from '@/components/BackLink';
import { TabBar } from '@/components/TabBar';
import { AlertBanner } from '@/components/AlertBanner';
import { OverviewTab } from '@/components/audit/OverviewTab';
import { ModuleTab } from '@/components/audit/ModuleTab';
import { AdminReportsSection } from '@/components/audit/AdminReportsSection';

// Heavy (three.js / 3d-force-graph) — code-split so it only loads when the tab opens.
const FilesystemMapTab = lazy(() => import('@/components/audit/FilesystemMapTab').then(m => ({ default: m.FilesystemMapTab })));

export default function AuditDetail() {
  const { auditId } = useParams();
  const { t } = useT();
  const { isGlobalAdmin } = useAuth();
  const nav = useNavigate();
  const { audit, modules, loading } = useAudit(auditId);
  const [tab, setTab] = useState('overview');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canDelete = isGlobalAdmin;

  const doDelete = async () => {
    if (!auditId) return;
    setDeleting(true);
    try {
      await callDeleteAudit({ auditId });
      toast.success(t('audit.delete.success'));
      nav('/dashboard');
    } catch (e) { toast.error((e as Error).message); setDeleting(false); }
  };

  const TABS = [
    { id: 'overview',             label: t('audit.tab.overview') },
    { id: 'ai_readiness',         label: t('audit.tab.ai_readiness') },
    { id: '01_hardware_compute',  label: t('audit.tab.01_hardware_compute') },
    { id: '02_compute_platforms', label: t('audit.tab.02_compute_platforms') },
    { id: '03_storage_access',    label: t('audit.tab.03_storage_access') },
    { id: '04_network',           label: t('audit.tab.04_network') },
    { id: '05_data_sources',      label: t('audit.tab.05_data_sources') },
    { id: '06_orchestration',     label: t('audit.tab.06_orchestration') },
    { id: '07_filesystem_map',    label: t('audit.tab.07_filesystem_map') },
    ...(isGlobalAdmin ? [{ id: 'admin_reports', label: t('reports.admin.section') }] : []),
  ];

  const hasFilesystemData = !!modules['07_filesystem_dirs'] || !!modules['07_filesystem_files'];

  if (loading) return <Loader fullscreen label={t('common.loading')} />;
  if (!audit) return <Card>{t('audit.not_found')}</Card>;

  const isProcessing = audit.status === 'uploading' || audit.status === 'processing';

  return (
    <div className="space-y-6">
      <BackLink to="/dashboard" label={t('audit.back')} />

      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h1 className="wrap-break-word font-display text-xl sm:text-2xl text-ink-900 dark:text-white">{audit.displayName}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-ink-500">
            <span className="inline-flex items-center gap-1"><Server className="h-3.5 w-3.5" /> {audit.systemName}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDate(audit.createdAt)}</span>
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.7rem] dark:bg-secondary">{audit.runId}</code>
            <Badge variant={statusBadgeVariant(audit.status)}>{t(STATUS_KEY[audit.status])}</Badge>
            {audit.totalElapsedSeconds != null && <span>{audit.totalElapsedSeconds}s</span>}
          </div>
        </div>
        <div className="flex gap-2 self-start lg:self-auto">
          <Link to="/documents" className={buttonClassName('secondary')}>{t('audit.export')}</Link>
          {canDelete && (
            <Button
              variant="destructiveOutline"
              onClick={() => setDeleteOpen(true)}
              title={t('audit.delete')}
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">{t('audit.delete')}</span>
            </Button>
          )}
        </div>
      </header>

      {audit.status === 'failed' && (
        <AlertBanner variant="danger" icon={<AlertTriangle className="h-4 w-4" />} title={t('audit.failed.title')}>
          <div className="text-xs">{audit.error?.message ?? t('audit.failed.desc')}</div>
          {canDelete && <div className="mt-2 text-xs">{t('audit.delete.failed_only')}</div>}
        </AlertBanner>
      )}

      {isProcessing && (
        <AlertBanner variant="warning" icon={<Loader2 className="h-4 w-4 animate-spin" />} title={t('audit.processing.title')}>
          <div className="text-xs">{t('audit.processing.desc')}</div>
        </AlertBanner>
      )}

      <TabBar
        tabs={TABS.map(tabItem => ({
          id: tabItem.id,
          label: tabItem.label,
          disabled: !(
            tabItem.id === 'overview'
            || tabItem.id === 'admin_reports'
            || (tabItem.id === '07_filesystem_map' ? hasFilesystemData : !!modules[tabItem.id])
          ) && !isProcessing,
        }))}
        activeTab={tab}
        onChange={id => startTransition(() => setTab(id))}
      />

      <div>
        {tab === 'overview'         && <OverviewTab audit={audit} />}
        {tab === 'admin_reports'    && isGlobalAdmin && <AdminReportsSection audit={audit} />}
        {tab === '07_filesystem_map' && (
          <Suspense fallback={<Loader label={t('common.loading')} />}>
            <FilesystemMapTab auditId={audit.id} modules={modules} />
          </Suspense>
        )}
        {tab !== 'overview' && tab !== 'admin_reports' && tab !== '07_filesystem_map' && (
          <ModuleTab moduleKey={tab} module={modules[tab]} />
        )}
      </div>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        title={t('audit.delete.confirm.title')}
        description={t('audit.delete.confirm.desc')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={doDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {t('audit.delete.confirm.cta')}
            </Button>
          </>
        }
      >
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
          <strong>{audit.displayName}</strong>
          <div className="mt-0.5 text-xs">{audit.systemName} · <code>{audit.runId}</code></div>
        </div>
      </Dialog>
    </div>
  );
}
